import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHamburger,
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faFilter,
  faSpinner,
  faTimes,
  faRefresh,
  faClose,
  faCheckCircle,
  faUtensils,
  faPizzaSlice,
  faWineBottle,
  faCookie,
  faImage,
  faTag,
  faDollarSign,
  faBoxOpen,
  faChartLine,
  faShoppingCart,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";

// API fetcher for JSON:API
const fetcher = async (url) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/vnd.api+json",
    },
  });
  if (!response.ok)
    throw new Error(`API request failed with status ${response.status}`);
  return response.json();
};

export default function MenuRegister() {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  const [formData, setFormData] = useState({
    title: "",
    price: "",
    description: "",
    availability: true,
    category: "",
    image: null,
    imageUrl: "",
  });
  const [editFormData, setEditFormData] = useState({
    title: "",
    price: "",
    description: "",
    availability: true,
    category: "",
    image: null,
    imageUrl: "",
    currentImageId: null,
  });

  // Fetch menu items
  const {
    data: menuData,
    error: menuError,
    isLoading: menuLoading,
    mutate: mutateMenu,
  } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/jsonapi/node/menu_item?sort=-created&include=field_category,field_image`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 30000,
    },
  );

  // Fetch categories
  const { data: categoriesData, mutate: mutateCategories } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/jsonapi/taxonomy_term/menu_category?sort=name`,
    fetcher,
    {
      revalidateOnFocus: true,
    },
  );

  // Fetch orders to calculate items sold
  const { data: ordersData } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/api/orders?period=all`,
    fetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 60000,
    },
  );

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
      },
    );
    if (!response.ok) throw new Error("Failed to fetch CSRF token");
    return await response.text();
  };

  // Upload image
  const uploadMenuImage = async (file) => {
    if (!file) return null;

    try {
      const csrfToken = await fetchCsrfToken();

      const uploadUrl = `${process.env.REACT_APP_BASE_URL}/file/upload/node/menu_item/field_image`;

      const response = await fetch(uploadUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/octet-stream",
          "Content-Disposition": `file; filename="${file.name}"`,
          "X-CSRF-Token": csrfToken,
        },
        body: file,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload error:", errorText);
        throw new Error(`File upload failed with status ${response.status}`);
      }

      const result = await response.json();
      console.log("Full upload response:", result);

      let fileUuid = null;

      if (result.uuid && result.uuid[0] && result.uuid[0].value) {
        fileUuid = result.uuid[0].value;
      } else if (result.data && result.data.id) {
        fileUuid = result.data.id;
      } else if (result.id) {
        fileUuid = result.id;
      } else if (result.uuid) {
        fileUuid = result.uuid;
      }

      if (!fileUuid) {
        console.error("Could not extract UUID from response:", result);
        throw new Error("Failed to extract file UUID from response");
      }

      console.log("Extracted file UUID:", fileUuid);

      return {
        id: fileUuid,
        type: "file--file",
      };
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  // Handle add new category
  const handleAddCategory = async (e) => {
    e.preventDefault();

    if (!newCategoryName.trim()) {
      setErrorMessage("Category name is required");
      setModalError(true);
      return;
    }

    setAddingCategory(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setAddingCategory(false);
      return;
    }

    const addCategoryPayload = {
      data: {
        type: "taxonomy_term--menu_category",
        attributes: {
          name: newCategoryName,
        },
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/jsonapi/taxonomy_term/menu_category`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(addCategoryPayload),
        },
      );

      if (response.ok) {
        const result = await response.json();
        console.log("Category created successfully:", result);

        await mutateCategories();

        const newCategoryId = result.data.id;
        setFormData({ ...formData, category: newCategoryId });

        setNewCategoryName("");
        setShowAddCategoryModal(false);
        setSuccessMessage("Category added successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        let errorMessageText = response.statusText;
        if (errorData.errors?.[0]?.detail) {
          errorMessageText = errorData.errors[0].detail;
        }
        console.error("Category creation error:", errorData);
        setErrorMessage(`Failed to create category: ${errorMessageText}`);
        setModalError(true);
      }
    } catch (err) {
      console.error("Error creating category:", err);
      setErrorMessage(
        err.message || "An error occurred while creating the category",
      );
      setModalError(true);
    }
    setAddingCategory(false);
  };

  // Calculate items sold from orders
  const calculateItemsSold = useMemo(() => {
    const salesMap = new Map();

    if (!ordersData?.data) return salesMap;

    ordersData.data.forEach((order) => {
      if (order.status === "paid" && order.items) {
        order.items.forEach((item) => {
          const currentSold = salesMap.get(item.name) || 0;
          salesMap.set(item.name, currentSold + item.quantity);
        });
      }
    });

    return salesMap;
  }, [ordersData]);

  // Process menu items with sold counts
  const menuItems = useMemo(() => {
    if (!menuData || !menuData.data) return [];

    const categoryLookup = {};
    if (categoriesData?.data) {
      categoriesData.data.forEach((cat) => {
        categoryLookup[cat.id] = cat.attributes.name;
      });
    }

    const imageLookup = {};
    if (menuData.included) {
      menuData.included.forEach((include) => {
        if (include.type === "file--file") {
          const imageUrl = include.attributes.uri?.url;
          if (imageUrl) {
            const fullUrl = imageUrl.startsWith("http")
              ? imageUrl
              : `${process.env.REACT_APP_BASE_URL}${imageUrl}`;
            imageLookup[include.id] = fullUrl;
          }
        }
      });
    }

    return menuData.data.map((item) => {
      const attributes = item.attributes;

      let category = "Uncategorized";
      let categoryId = null;
      if (item.relationships?.field_category?.data) {
        categoryId = item.relationships.field_category.data.id;
        category = categoryLookup[categoryId] || "Uncategorized";
      }

      let imageUrl = null;
      let imageId = null;
      if (item.relationships?.field_image?.data) {
        imageId = item.relationships.field_image.data.id;
        imageUrl = imageLookup[imageId] || null;
      }

      const itemsSold = calculateItemsSold.get(attributes.title) || 0;

      return {
        id: item.id,
        nid: attributes.drupal_internal__nid,
        title: attributes.title,
        price: parseFloat(attributes.field_price) || 0,
        description: attributes.field_description || "",
        availability: attributes.field_availability,
        category: category,
        categoryId: categoryId,
        imageUrl: imageUrl,
        imageId: imageId,
        itemsSold: itemsSold,
        created: attributes.created,
        changed: attributes.changed,
      };
    });
  }, [menuData, categoriesData, calculateItemsSold]);

  // Calculate category statistics
  const categoryStatistics = useMemo(() => {
    const stats = new Map();

    menuItems.forEach((item) => {
      if (!stats.has(item.category)) {
        stats.set(item.category, {
          category: item.category,
          totalItems: 0,
          availableItems: 0,
          totalSold: 0,
          totalRevenue: 0,
        });
      }

      const catStats = stats.get(item.category);
      catStats.totalItems++;
      if (item.availability) catStats.availableItems++;
      catStats.totalSold += item.itemsSold;
      catStats.totalRevenue += item.itemsSold * item.price;
    });

    return Array.from(stats.values()).sort((a, b) => b.totalSold - a.totalSold);
  }, [menuItems]);

  // Filter menu items
  const filteredItems = useMemo(() => {
    let filtered = [...menuItems];

    if (categoryFilter !== "all") {
      filtered = filtered.filter((item) => item.categoryId === categoryFilter);
    }

    if (availabilityFilter !== "all") {
      filtered = filtered.filter((item) =>
        availabilityFilter === "available"
          ? item.availability
          : !item.availability,
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.description &&
            item.description.toLowerCase().includes(searchTerm.toLowerCase())),
      );
    }

    return filtered;
  }, [menuItems, categoryFilter, availabilityFilter, searchTerm]);

  // Calculate overall statistics
  const statistics = useMemo(() => {
    const totalItems = menuItems.length;
    const availableItems = menuItems.filter((i) => i.availability).length;
    const unavailableItems = menuItems.filter((i) => !i.availability).length;
    const totalValue = menuItems.reduce((sum, i) => sum + i.price, 0);
    const avgPrice = totalItems > 0 ? totalValue / totalItems : 0;
    const totalItemsSold = menuItems.reduce((sum, i) => sum + i.itemsSold, 0);
    const totalRevenue = menuItems.reduce(
      (sum, i) => sum + i.itemsSold * i.price,
      0,
    );

    return {
      totalItems,
      availableItems,
      unavailableItems,
      totalValue,
      avgPrice,
      totalItemsSold,
      totalRevenue,
    };
  }, [menuItems]);

  // Pagination logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentPage, itemsPerPage]);

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, availabilityFilter]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Add menu item
  const handleAddItem = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setErrorMessage("Item name is required");
      setModalError(true);
      return;
    }

    if (!formData.price || formData.price <= 0) {
      setErrorMessage("Valid price is required");
      setModalError(true);
      return;
    }

    if (!formData.category) {
      setErrorMessage("Please select a category");
      setModalError(true);
      return;
    }

    setIsSubmitting(true);

    let fileData = null;

    if (formData.image) {
      console.log("Starting menu item image upload...");
      setUploadingImage(true);

      try {
        fileData = await uploadMenuImage(formData.image);
        if (fileData && fileData.id) {
          console.log("Menu Image UUID captured:", fileData.id);
        } else {
          console.warn("Image upload failed, proceeding without image.");
        }
      } catch (error) {
        console.error("Image upload error:", error);
        setErrorMessage("Failed to upload image: " + error.message);
        setModalError(true);
        setUploadingImage(false);
        setIsSubmitting(false);
        return;
      }

      setUploadingImage(false);
    }

    try {
      const csrfToken = await fetchCsrfToken();

      const payload = {
        data: {
          type: "node--menu_item",
          attributes: {
            title: formData.title,
            field_price: parseFloat(formData.price).toFixed(2),
            field_description: formData.description || "",
            field_availability: formData.availability,
          },
          relationships: {
            field_category: {
              data: {
                type: "taxonomy_term--menu_category",
                id: formData.category,
              },
            },
          },
        },
      };

      if (fileData && fileData.id) {
        payload.data.relationships.field_image = {
          data: {
            type: "file--file",
            id: fileData.id,
          },
        };
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/jsonapi/node/menu_item`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        await mutateMenu();
        setFormData({
          title: "",
          price: "",
          description: "",
          availability: true,
          category: "",
          image: null,
          imageUrl: "",
        });
        setShowAddModal(false);
        setSuccessMessage(`${formData.title} added successfully!`);
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.errors?.[0]?.detail || "Failed to add item");
        setModalError(true);
      }
    } catch (err) {
      console.error("Error adding item:", err);
      setErrorMessage("An error occurred while adding the item");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  // Edit menu item
  const handleEditItem = async (e) => {
    e.preventDefault();

    if (!editFormData.title.trim()) {
      setErrorMessage("Item name is required");
      setModalError(true);
      return;
    }

    if (!editFormData.price || editFormData.price <= 0) {
      setErrorMessage("Valid price is required");
      setModalError(true);
      return;
    }

    if (!editFormData.category) {
      setErrorMessage("Please select a category");
      setModalError(true);
      return;
    }

    setIsSubmitting(true);

    let fileData = null;

    if (editFormData.image && typeof editFormData.image === "object") {
      console.log("Starting menu item image upload for edit...");
      setUploadingImage(true);

      try {
        fileData = await uploadMenuImage(editFormData.image);
        if (fileData && fileData.id) {
          console.log("Menu Image UUID captured:", fileData.id);
        }
      } catch (error) {
        console.error("Image upload error:", error);
        setErrorMessage("Failed to upload image: " + error.message);
        setModalError(true);
        setUploadingImage(false);
        setIsSubmitting(false);
        return;
      }

      setUploadingImage(false);
    }

    try {
      const csrfToken = await fetchCsrfToken();

      const payload = {
        data: {
          type: "node--menu_item",
          id: selectedItem.id,
          attributes: {
            title: editFormData.title,
            field_price: parseFloat(editFormData.price).toFixed(2),
            field_description: editFormData.description || "",
            field_availability: editFormData.availability,
          },
          relationships: {
            field_category: {
              data: {
                type: "taxonomy_term--menu_category",
                id: editFormData.category,
              },
            },
          },
        },
      };

      if (fileData && fileData.id) {
        payload.data.relationships.field_image = {
          data: {
            type: "file--file",
            id: fileData.id,
          },
        };
      } else if (editFormData.currentImageId && !editFormData.image) {
        payload.data.relationships.field_image = {
          data: {
            type: "file--file",
            id: editFormData.currentImageId,
          },
        };
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/jsonapi/node/menu_item/${selectedItem.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        await mutateMenu();
        setShowEditModal(false);
        setSelectedItem(null);
        setEditFormData({
          title: "",
          price: "",
          description: "",
          availability: true,
          category: "",
          image: null,
          imageUrl: "",
          currentImageId: null,
        });
        setSuccessMessage(`${editFormData.title} updated successfully!`);
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        setErrorMessage(
          errorData.errors?.[0]?.detail || "Failed to update item",
        );
        setModalError(true);
      }
    } catch (err) {
      console.error("Error updating item:", err);
      setErrorMessage("An error occurred while updating the item");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  // Delete menu item
  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.title}"?`)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const csrfToken = await fetchCsrfToken();

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/jsonapi/node/menu_item/${item.id}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
        },
      );

      if (response.ok) {
        await mutateMenu();
        setSuccessMessage(`${item.title} deleted successfully!`);
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        setErrorMessage(
          errorData.errors?.[0]?.detail || "Failed to delete item",
        );
        setModalError(true);
      }
    } catch (err) {
      console.error("Error deleting item:", err);
      setErrorMessage("An error occurred while deleting the item");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setEditFormData({
      title: item.title,
      price: item.price,
      description: item.description || "",
      availability: item.availability,
      category: item.categoryId,
      image: null,
      imageUrl: item.imageUrl,
      currentImageId: item.imageId,
    });
    setShowEditModal(true);
  };

  const MenuCard = ({ item }) => {
    const categoryIcon =
      item.category === "Mains"
        ? faUtensils
        : item.category === "Pizzas"
          ? faPizzaSlice
          : item.category === "Drinks"
            ? faWineBottle
            : item.category === "Desserts"
              ? faCookie
              : faHamburger;

    return (
      <div style={styles.menuCard}>
        <div style={styles.menuImageContainer}>
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item.title}
              style={styles.menuImage}
            />
          ) : (
            <div style={styles.menuImagePlaceholder}>
              <FontAwesomeIcon
                icon={faImage}
                style={{ fontSize: "32px", color: "#CBD5E1" }}
              />
            </div>
          )}
          <div
            style={
              item.availability
                ? styles.availableBadge
                : styles.unavailableBadge
            }
          >
            {item.availability ? "Available" : "Unavailable"}
          </div>
        </div>

        <div style={styles.menuContent}>
          <div style={styles.menuHeader}>
            <h3 style={styles.menuTitle}>{item.title}</h3>
            <div style={styles.menuPrice}>GH₵{item.price.toFixed(2)}</div>
          </div>

          <div style={styles.menuCategory}>
            <FontAwesomeIcon icon={categoryIcon} style={{ fontSize: "10px" }} />
            <span>{item.category}</span>
          </div>

          <div style={styles.menuSoldStats}>
            <div style={styles.soldStat}>
              <FontAwesomeIcon
                icon={faShoppingCart}
                style={{ fontSize: "11px", color: "#FF6B35" }}
              />
              <span>{item.itemsSold} sold</span>
            </div>
            <div style={styles.revenueStat}>
              <FontAwesomeIcon
                icon={faDollarSign}
                style={{ fontSize: "11px", color: "#10B981" }}
              />
              <span>GH₵{(item.itemsSold * item.price).toFixed(2)}</span>
            </div>
          </div>

          {item.description && (
            <p style={styles.menuDescription}>{item.description}</p>
          )}

          <div style={styles.menuActions}>
            <button
              style={styles.editButton}
              onClick={() => openEditModal(item)}
            >
              <FontAwesomeIcon icon={faEdit} /> Edit
            </button>
            <button
              style={styles.deleteButton}
              onClick={() => handleDeleteItem(item)}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (menuLoading && !menuData) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: "48px", color: "#FF6B35" }}
              />
              <p>Loading menu items...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (menuError) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.errorContainer}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ fontSize: "48px", color: "#d32f2f" }}
              />
              <h3>Error Loading Menu</h3>
              <p>
                Unable to connect to the menu API. Please check your connection.
              </p>
              <button style={styles.retryButton} onClick={() => mutateMenu()}>
                <FontAwesomeIcon
                  icon={faRefresh}
                  style={{ marginRight: "8px" }}
                />
                Retry
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          <TopActionMenus AddTitle="Menu Register" />

          {/* STATISTICS CARDS - Row 1 */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E8F5E9",
                }}
              >
                <FontAwesomeIcon
                  icon={faHamburger}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalItems}</h3>
                <p style={styles.statLabel}>Total Items</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#D1FAE5",
                }}
              >
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{ color: "#10B981", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.availableItems}</h3>
                <p style={styles.statLabel}>Available</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEE2E2",
                }}
              >
                <FontAwesomeIcon
                  icon={faBoxOpen}
                  style={{ color: "#EF4444", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.unavailableItems}</h3>
                <p style={styles.statLabel}>Unavailable</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E3F2FD",
                }}
              >
                <FontAwesomeIcon
                  icon={faDollarSign}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  GH₵{statistics.avgPrice.toFixed(2)}
                </h3>
                <p style={styles.statLabel}>Average Price</p>
              </div>
            </div>
          </div>

          {/* STATISTICS CARDS - Row 2 */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEF3F2",
                }}
              >
                <FontAwesomeIcon
                  icon={faShoppingCart}
                  style={{ color: "#FF6B35", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalItemsSold}</h3>
                <p style={styles.statLabel}>Total Items Sold</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FCE4EC",
                }}
              >
                <FontAwesomeIcon
                  icon={faChartLine}
                  style={{ color: "#E91E63", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  GH₵{statistics.totalRevenue.toFixed(2)}
                </h3>
                <p style={styles.statLabel}>Total Revenue</p>
              </div>
            </div>

            {/* Empty cards for alignment (optional) */}
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#F3F4F6",
                }}
              >
                <FontAwesomeIcon
                  icon={faTag}
                  style={{ color: "#9CA3AF", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {categoriesData?.data?.length || 0}
                </h3>
                <p style={styles.statLabel}>Categories</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#F3F4F6",
                }}
              >
                <FontAwesomeIcon
                  icon={faChartLine}
                  style={{ color: "#9CA3AF", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>-</h3>
                <p style={styles.statLabel}>-</p>
              </div>
            </div>
          </div>

          {/* CATEGORY STATISTICS CARDS */}
          <div style={styles.categoryStatsSection}>
            <h3 style={styles.sectionTitle}>Category Performance</h3>
            <div style={styles.categoryStatsGrid}>
              {categoryStatistics.map((cat) => (
                <div key={cat.category} style={styles.categoryStatCard}>
                  <div style={styles.categoryStatHeader}>
                    <FontAwesomeIcon
                      icon={faTag}
                      style={{ color: "#FF6B35" }}
                    />
                    <span style={styles.categoryStatName}>{cat.category}</span>
                  </div>
                  <div style={styles.categoryStatContent}>
                    <div style={styles.categoryStatItem}>
                      <span style={styles.categoryStatLabel}>Items:</span>
                      <span style={styles.categoryStatValue}>
                        {cat.totalItems}
                      </span>
                    </div>
                    <div style={styles.categoryStatItem}>
                      <span style={styles.categoryStatLabel}>Available:</span>
                      <span style={styles.categoryStatValue}>
                        {cat.availableItems}
                      </span>
                    </div>
                    <div style={styles.categoryStatItem}>
                      <span style={styles.categoryStatLabel}>Sold:</span>
                      <span style={styles.categoryStatValue}>
                        {cat.totalSold}
                      </span>
                    </div>
                    <div style={styles.categoryStatItem}>
                      <span style={styles.categoryStatLabel}>Revenue:</span>
                      <span style={styles.categoryStatValue}>
                        GH₵{cat.totalRevenue.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FILTERS BAR */}
          <div style={styles.filtersBar}>
            <div style={styles.searchWrapper}>
              <FontAwesomeIcon icon={faSearch} style={{ color: "#9CA3AF" }} />
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Search menu items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div style={styles.filterWrapper}>
              <FontAwesomeIcon icon={faFilter} style={{ color: "#9CA3AF" }} />
              <select
                style={styles.filterSelect}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categoriesData?.data?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.attributes.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.filterWrapper}>
              <FontAwesomeIcon icon={faTag} style={{ color: "#9CA3AF" }} />
              <select
                style={styles.filterSelect}
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
              </select>
            </div>

            <button
              style={styles.addButton}
              onClick={() => setShowAddModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> Add Menu Item
            </button>

            <button style={styles.refreshButton} onClick={() => mutateMenu()}>
              <FontAwesomeIcon icon={faRefresh} />
            </button>
          </div>

          {/* Total Items Count */}
          <div style={styles.itemsCountBar}>
            <span style={styles.itemsCountText}>
              Showing {paginatedItems.length} of {filteredItems.length} items
              {searchTerm && ` (filtered from ${menuItems.length} total)`}
            </span>
          </div>

          {/* MENU GRID */}
          {filteredItems.length === 0 ? (
            <div style={styles.emptyState}>
              <FontAwesomeIcon
                icon={faHamburger}
                style={{ fontSize: "64px", color: "#CBD5E1" }}
              />
              <h3 style={styles.emptyTitle}>No Menu Items Found</h3>
              <p style={styles.emptySubtitle}>
                {searchTerm ||
                categoryFilter !== "all" ||
                availabilityFilter !== "all"
                  ? "No items match your current filters"
                  : "Click 'Add Menu Item' to create your first menu item"}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.menuGrid}>
                {paginatedItems.map((item) => (
                  <MenuCard key={item.id} item={item} />
                ))}
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div style={styles.paginationContainer}>
                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    }}
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} /> Previous
                  </button>

                  <div style={styles.paginationPages}>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 7) {
                        pageNum = i + 1;
                      } else if (currentPage <= 4) {
                        pageNum = i + 1;
                        if (i === 6) pageNum = totalPages;
                      } else if (currentPage >= totalPages - 3) {
                        pageNum = totalPages - 6 + i;
                      } else {
                        pageNum = currentPage - 3 + i;
                      }
                      return pageNum;
                    }).map((pageNum, index, array) => {
                      // Show ellipsis
                      if (index > 0 && pageNum - array[index - 1] > 1) {
                        return (
                          <span
                            key={`ellipsis-${index}`}
                            style={styles.paginationEllipsis}
                          >
                            …
                          </span>
                        );
                      }
                      return (
                        <button
                          key={pageNum}
                          style={{
                            ...styles.paginationPageButton,
                            ...(currentPage === pageNum
                              ? styles.paginationPageButtonActive
                              : {}),
                          }}
                          onClick={() => goToPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                    }}
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <DashboardContentFooter />

      {/* Add Menu Item Modal */}
      {showAddModal && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content" style={{ maxWidth: "550px" }}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Add New Menu Item</p>
                <button
                  className="close-modal"
                  onClick={() => setShowAddModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleAddItem} className="edit-patient-form">
                <div className="form-group">
                  <label>Item Name *</label>
                  <input
                    type="text"
                    placeholder="Enter item name"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <div style={styles.selectWithButton}>
                    <select
                      style={styles.selectField}
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                      required
                    >
                      <option value="">Select Category</option>
                      {categoriesData?.data?.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.attributes.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      style={styles.inlineAddButton}
                      onClick={() => setShowAddCategoryModal(true)}
                      title="Add New Category"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Price (GH₵) *</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Status</label>
                    <select
                      value={formData.availability}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          availability: e.target.value === "true",
                        })
                      }
                    >
                      <option value="true">Available</option>
                      <option value="false">Unavailable</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Enter item description"
                    rows="3"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Item Image</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFormData({
                          ...formData,
                          image: file,
                          imageUrl: URL.createObjectURL(file),
                        });
                      }
                    }}
                  />
                  {formData.imageUrl && (
                    <div style={styles.imagePreview}>
                      <img
                        src={formData.imageUrl}
                        alt="Preview"
                        style={styles.previewImage}
                      />
                      <p style={styles.previewText}>
                        Preview - will be uploaded
                      </p>
                    </div>
                  )}
                </div>

                <div className="submitGroup">
                  <button
                    type="submit"
                    disabled={isSubmitting || uploadingImage}
                    style={styles.submitButton}
                  >
                    {isSubmitting || uploadingImage ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Add Menu Item"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content" style={{ maxWidth: "400px" }}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Add New Category</p>
                <button
                  className="close-modal"
                  onClick={() => {
                    setShowAddCategoryModal(false);
                    setNewCategoryName("");
                  }}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleAddCategory} className="edit-patient-form">
                <div className="form-group">
                  <label>Category Name *</label>
                  <input
                    type="text"
                    placeholder="Enter Category Name"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                <div className="submitGroup">
                  <button
                    type="submit"
                    disabled={addingCategory}
                    style={styles.submitButton}
                  >
                    {addingCategory ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Add Category"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Menu Item Modal */}
      {showEditModal && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content" style={{ maxWidth: "550px" }}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Edit Menu Item</p>
                <button
                  className="close-modal"
                  onClick={() => setShowEditModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleEditItem} className="edit-patient-form">
                <div className="form-group">
                  <label>Item Name *</label>
                  <input
                    type="text"
                    placeholder="Enter item name"
                    value={editFormData.title}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        title: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <div style={styles.selectWithButton}>
                    <select
                      style={styles.selectField}
                      value={editFormData.category}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          category: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="">Select Category</option>
                      {categoriesData?.data?.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.attributes.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      style={styles.inlineAddButton}
                      onClick={() => setShowAddCategoryModal(true)}
                      title="Add New Category"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Price (GH₵) *</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={editFormData.price}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          price: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Status</label>
                    <select
                      value={editFormData.availability}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          availability: e.target.value === "true",
                        })
                      }
                    >
                      <option value="true">Available</option>
                      <option value="false">Unavailable</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Enter item description"
                    rows="3"
                    value={editFormData.description}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        description: e.target.value,
                      })
                    }
                  />
                </div>

                {editFormData.imageUrl && (
                  <div className="form-group">
                    <label>Current Image</label>
                    <div style={styles.imagePreview}>
                      <img
                        src={editFormData.imageUrl}
                        alt="Current"
                        style={styles.previewImage}
                      />
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Upload New Image (Optional)</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setEditFormData({
                          ...editFormData,
                          image: file,
                          imageUrl: URL.createObjectURL(file),
                        });
                      }
                    }}
                  />
                  {editFormData.image && (
                    <div style={styles.imagePreview}>
                      <img
                        src={editFormData.imageUrl}
                        alt="New Preview"
                        style={styles.previewImage}
                      />
                      <p style={styles.previewText}>
                        New image preview - will replace current
                      </p>
                    </div>
                  )}
                </div>

                <div className="submitGroup">
                  <button
                    type="submit"
                    disabled={isSubmitting || uploadingImage}
                    style={styles.submitButton}
                  >
                    {isSubmitting || uploadingImage ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Update Menu Item"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faTimes}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    color: "#d32f2f",
  },
  retryButton: {
    marginTop: "20px",
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    marginBottom: "24px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  statIconContainer: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "700",
    color: "#1F2937",
  },
  statLabel: {
    margin: "4px 0 0 0",
    color: "#6B7280",
    fontSize: "14px",
    fontWeight: "500",
  },
  categoryStatsSection: {
    marginBottom: "24px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1F2937",
    marginBottom: "16px",
  },
  categoryStatsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: "16px",
  },
  categoryStatCard: {
    background: "white",
    padding: "16px",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  categoryStatHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "12px",
    marginBottom: "12px",
    borderBottom: "1px solid #E5E7EB",
  },
  categoryStatName: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1F2937",
  },
  categoryStatContent: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "8px",
  },
  categoryStatItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  categoryStatLabel: {
    fontSize: "11px",
    color: "#6B7280",
  },
  categoryStatValue: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#1F2937",
  },
  filtersBar: {
    display: "flex",
    gap: "16px",
    marginBottom: "16px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchWrapper: {
    flex: 2,
    minWidth: "250px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  searchInput: {
    flex: 1,
    border: "none",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "transparent",
  },
  filterWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 16px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  filterSelect: {
    border: "none",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    padding: "4px 8px",
  },
  selectWithButton: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  selectField: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
  },
  inlineAddButton: {
    background: "#28a745",
    color: "white",
    padding: "8px 12px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  addButton: {
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  refreshButton: {
    padding: "10px 16px",
    backgroundColor: "white",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    cursor: "pointer",
    color: "#6B7280",
  },
  itemsCountBar: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "16px",
    padding: "8px 0",
  },
  itemsCountText: {
    fontSize: "14px",
    color: "#6B7280",
    fontWeight: "500",
  },
  menuGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "20px",
    marginBottom: "24px",
  },
  menuCard: {
    backgroundColor: "white",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  menuImageContainer: {
    position: "relative",
    height: "180px",
    backgroundColor: "#F9FAFB",
  },
  menuImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  menuImagePlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F3F4F6",
  },
  availableBadge: {
    position: "absolute",
    top: "12px",
    right: "12px",
    backgroundColor: "#10B981",
    color: "white",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
  },
  unavailableBadge: {
    position: "absolute",
    top: "12px",
    right: "12px",
    backgroundColor: "#EF4444",
    color: "white",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
  },
  menuContent: {
    padding: "16px",
  },
  menuHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "8px",
  },
  menuTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600",
    color: "#1F2937",
  },
  menuPrice: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#FF6B35",
  },
  menuCategory: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    backgroundColor: "#F3F4F6",
    borderRadius: "20px",
    fontSize: "11px",
    color: "#6B7280",
    marginBottom: "10px",
    width: "fit-content",
  },
  menuSoldStats: {
    display: "flex",
    gap: "16px",
    marginBottom: "10px",
    padding: "8px 0",
    borderTop: "1px solid #F0F0F0",
    borderBottom: "1px solid #F0F0F0",
  },
  soldStat: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: "500",
    color: "#FF6B35",
  },
  revenueStat: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: "500",
    color: "#10B981",
  },
  menuDescription: {
    fontSize: "13px",
    color: "#6B7280",
    margin: "0 0 12px 0",
    lineHeight: "1.4",
  },
  menuActions: {
    display: "flex",
    gap: "12px",
    paddingTop: "12px",
    borderTop: "1px solid #E5E7EB",
  },
  editButton: {
    flex: 1,
    padding: "8px",
    backgroundColor: "#E3F2FD",
    color: "#1976D2",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  deleteButton: {
    flex: 1,
    padding: "8px",
    backgroundColor: "#FEE2E2",
    color: "#D32F2F",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1F2937",
    marginTop: "16px",
  },
  emptySubtitle: {
    fontSize: "14px",
    color: "#9CA3AF",
    marginTop: "8px",
  },
  formRow: {
    display: "flex",
    gap: "16px",
  },
  imagePreview: {
    marginTop: "10px",
  },
  previewImage: {
    width: "100px",
    height: "100px",
    objectFit: "cover",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
  },
  previewText: {
    fontSize: "11px",
    color: "#6B7280",
    marginTop: "4px",
  },
  submitButton: {
    background: "#FF6B35",
    color: "white",
    padding: "10px 24px",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
  // Pagination Styles
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
    padding: "20px",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginTop: "8px",
  },
  paginationButton: {
    padding: "10px 20px",
    backgroundColor: "#F3F4F6",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#1F2937",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
  },
  paginationPages: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  paginationPageButton: {
    padding: "8px 14px",
    backgroundColor: "white",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#1F2937",
    transition: "all 0.2s ease",
    minWidth: "40px",
    textAlign: "center",
  },
  paginationPageButtonActive: {
    backgroundColor: "#FF6B35",
    color: "white",
    borderColor: "#FF6B35",
  },
  paginationEllipsis: {
    padding: "8px 4px",
    fontSize: "14px",
    color: "#6B7280",
  },
};
